// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2)
// version  : 2.0
// updated  : 2026-06-20
// protected: NO  -> Halaman Permintaan Merchandize V2.
// changelog: v2.0 (Fase 6A) - Permintaan Merchandize MULTI-SIZE (pola Work Order):
//            - tiap baris pilih Merchandize + Jenis Stok (JUAL/MoU)
//            - Size grid S..7XL (has_size_variants) atau Quantity total (tanpa size)
//            - Regional lock: Staff Store hanya bisa minta Merchandize region-nya
//              (difilter di UI + ditegakkan backend). Label user-facing: "Merchandize".
// =============================================================================
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, CategoryBadge } from "@/v2/components/statusMap";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, Send, Pencil, X, Check, Ban, ClipboardList, FileText, Truck, DoorClosed, PackageSearch, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { useV2LiveRefresh } from "@/v2/V2RealtimeContext";
import { openPdf } from "@/v2/components/helpers";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";

const STORE = ["STAFF_STORE_AREMA", "STAFF_STORE_BORNEO"];
const KACAB = ["KACAB_SBY", "KACAB_SMD"];
const REGION_BY_STORE_ROLE = { STAFF_STORE_AREMA: "SBY", STAFF_STORE_BORNEO: "SMD" };
const REGION_LABEL = { SBY: "Surabaya", SMD: "Samarinda", GLOBAL: "-" };
const SIZES = ["S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL", "7XL"];

const STATUS_LABEL = {
  DRAFT: "Draft", PENDING_APPROVAL: "Menunggu Persetujuan", APPROVED: "Disetujui",
  MENUNGGU_PRODUKSI: "Menunggu Produksi (Stok Kurang)", READY_TO_SHIP: "Siap Diproses Pengiriman",
  REJECTED: "Ditolak", CANCELLED: "Dibatalkan", CLOSED: "Ditutup",
};
// Keluarga "disetujui & aktif" (P3): tampilkan remaining/aksi pengiriman/tutup.
const APPROVED_FAMILY = ["APPROVED", "MENUNGGU_PRODUKSI", "READY_TO_SHIP"];
const SHIPPABLE = ["APPROVED", "READY_TO_SHIP"];

const fmtTs = (s) => {
  if (!s) return null;
  try { return new Date(s).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }); }
  catch (e) { return s; }
};
const rid = () => Math.random().toString(36).slice(2, 9);
const newLine = () => ({ key: rid(), product_id: "", item_category: "Jual", sizes: {}, qtyNoSize: "" });
const ownerOf = (p) => (p ? (p.owner_region || p.region) : null);

export default function PermintaanPage() {
  const { me, api } = useV2Auth();
  const role = me?.role;
  const canCreate = STORE.includes(role);
  const canApprove = KACAB.includes(role);
  const myRegion = me?.region || REGION_BY_STORE_ROLE[role] || null;

  const [reqs, setReqs] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize("store-requests"));
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const listTopRef = useRef(null);
  const pageRef = useRef(1);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // form (create / revise)
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);  // C.1: form Buat/Revisi permintaan
  const [busyAct, setBusyAct] = useState(null);          // C.1: aksi baris (`${id}:${action}`)
  const [lines, setLines] = useState([newLine()]);
  const [note, setNote] = useState("");

  // reject dialog
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  // detail dialog
  const [detail, setDetail] = useState(null);
  // UAT-05: stock-check dialog + close dialog
  const navigate = useNavigate();
  const [stock, setStock] = useState(null);          // hasil /stock-check
  const [stockLoading, setStockLoading] = useState(false);
  const [closeId, setCloseId] = useState(null);
  const [closeReason, setCloseReason] = useState("");

  const loadList = useCallback(async (p, size = pageSize) => {
    const target = p || pageRef.current || 1;
    setLoading(true);
    try {
      const r = await api(`/api/v2/store-requests?page=${target}&page_size=${size}`);
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setReqs(data.items || []);
        setMeta({ total: data.total || 0, pages: data.pages || 1 });
        setPage(data.page || target); pageRef.current = data.page || target;
      } else setReqs([]);
    } finally { setLoading(false); }
  }, [api, pageSize]);
  // dipakai useEffect awal, live-refresh, & sesudah aksi: pertahankan halaman aktif.
  const load = useCallback(() => loadList(pageRef.current), [loadList]);
  const goPage = (p) => { loadList(p); if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const changePageSize = (s) => { setPageSize(s); loadList(1, s); };

  const loadProducts = useCallback(async () => {
    if (!canCreate) return;
    const r = await api("/api/v2/merchandise/products");
    if (r.ok) setProducts((await r.json()).items || []);
  }, [api, canCreate]);

  useEffect(() => { load(); loadProducts(); }, [load, loadProducts]);
  useV2LiveRefresh(load);  // FASE 7B (refresh daftar; form input tak tersentuh)

  // SLICE S-A (A-4): terima ?product=<id> dari notifikasi stok -> PRA-PILIH produk di baris
  // pertama. TANPA param -> identik. Param tak dikenal -> diabaikan. Tidak membuat dokumen.
  const [searchParams] = useSearchParams();
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (prefilled || !canCreate) return;
    const pid = searchParams.get("product");
    if (!pid || !products.length) return;
    if (!products.some((p) => p.id === pid)) { setPrefilled(true); return; }
    setLines((prev) => (prev.length === 1 && !prev[0].product_id
      ? [{ ...prev[0], product_id: pid }] : prev));
    setPrefilled(true);
  }, [searchParams, products, canCreate, prefilled]);

  // Regional lock di UI: hanya Merchandize milik region store ini (backend tetap enforce).
  const myProducts = products.filter((p) => ownerOf(p) === myRegion);
  const productById = (id) => products.find((p) => p.id === id);
  const prodName = (id) => productById(id)?.name || id?.slice(0, 8);

  const resetForm = () => { setEditId(null); setLines([newLine()]); setNote(""); };

  const setLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls));
  const setSizeQty = (key, sz, val) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, sizes: { ...l.sizes, [sz]: val } } : l)));

  const lineTotal = (l) => {
    const p = productById(l.product_id);
    if (p && !p.has_size_variants) return Number(l.qtyNoSize || 0);
    return SIZES.reduce((s, sz) => s + Number(l.sizes[sz] || 0), 0);
  };

  const buildPayloadLines = () => {
    const out = [];
    for (const l of lines) {
      if (!l.product_id) continue;
      const p = productById(l.product_id);
      const cat = l.item_category || "Jual";
      if (p && !p.has_size_variants) {
        const q = Number(l.qtyNoSize || 0);
        if (q > 0) out.push({ product_id: l.product_id, size: "-", qty: q, item_category: cat });
      } else {
        for (const sz of SIZES) {
          const q = Number(l.sizes[sz] || 0);
          if (q > 0) out.push({ product_id: l.product_id, size: sz, qty: q, item_category: cat });
        }
      }
    }
    return out;
  };

  const startRevise = (req) => {
    setEditId(req.id);
    setNote(req.note || "");
    // Rekonstruksi builder: kelompokkan line lama per (product_id + item_category).
    const groups = {};
    (req.lines || []).forEach((l) => {
      const k = `${l.product_id}|${l.item_category || "Jual"}`;
      if (!groups[k]) groups[k] = { key: rid(), product_id: l.product_id || "", item_category: l.item_category || "Jual", sizes: {}, qtyNoSize: "" };
      const p = productById(l.product_id);
      if (p && !p.has_size_variants) groups[k].qtyNoSize = String((Number(groups[k].qtyNoSize) || 0) + Number(l.qty || 0));
      else groups[k].sizes[l.size] = String((Number(groups[k].sizes[l.size]) || 0) + Number(l.qty || 0));
    });
    const arr = Object.values(groups);
    setLines(arr.length ? arr : [newLine()]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitForm = async () => {
    const payloadLines = buildPayloadLines();
    if (!payloadLines.length) { toast.error("Pilih minimal 1 Merchandize dengan Quantity > 0"); return; }
    if (submitting) return;  // C.1: cegah klik ganda
    setSubmitting(true);
    try {
      const payload = { lines: payloadLines, note: note || null };
      const r = editId
        ? await api(`/api/v2/store-requests/${editId}`, { method: "PUT", body: JSON.stringify(payload) })
        : await api("/api/v2/store-requests", { method: "POST", body: JSON.stringify(payload) });
      if (r.ok) {
        toast.success(editId ? "Permintaan direvisi" : "Permintaan dibuat (Draft)");
        resetForm(); load();
      } else { toast.error((await r.json()).detail || "Gagal menyimpan permintaan"); }
    } finally {
      setSubmitting(false);
    }
  };

  const act = async (id, action, body) => {
    const key = `${id}:${action}`;
    if (busyAct) return;  // C.1: cegah aksi ganda/beruntun
    setBusyAct(key);
    try {
      const r = await api(`/api/v2/store-requests/${id}/${action}`,
        { method: "POST", body: body ? JSON.stringify(body) : undefined });
      if (r.ok) {
        const d = await r.json();
        if (action === "approve") {
          const short = d?.stock?.total_short ?? 0;
          if (d?.status === "MENUNGGU_PRODUKSI" || short > 0) {
            toast.warning(`Disetujui, tetapi stok gudang kurang ${short} pcs. Status: Menunggu Produksi — Admin Produksi perlu membuat Work Order.`);
          } else {
            toast.success("Disetujui — stok cukup. Silakan Lanjutkan ke Pengiriman Store.");
          }
        } else if (action === "close") {
          toast.success("Permintaan ditutup");
        } else if (action === "recheck-stock") {
          toast[d?.status === "READY_TO_SHIP" ? "success" : "info"](
            d?.status === "READY_TO_SHIP"
              ? "Stok kini cukup — Permintaan Siap Diproses Pengiriman."
              : `Stok masih kurang ${d?.stock?.total_short ?? 0} pcs — tetap menunggu produksi.`);
        } else {
          toast.success("Berhasil");
        }
        load();
      } else { toast.error((await r.json()).detail || `Gagal ${action}`); }
    } finally {
      setBusyAct(null);
    }
  };
  const isBusy = (id, action) => busyAct === `${id}:${action}`;

  const doReject = async () => {
    await act(rejectId, "reject", { reason: rejectReason });
    setRejectId(null); setRejectReason("");
  };

  // UAT-05: bangun rows pre-fill untuk halaman Pengiriman Store (shape sama dgn RequestLineEditor).
  const buildPrefillRows = (req) => {
    const groups = {};
    (req.lines || []).forEach((l) => {
      const k = `${l.product_id}|${l.item_category || "Jual"}`;
      if (!groups[k]) groups[k] = { key: rid(), product_id: l.product_id || "", item_category: l.item_category || "Jual", sizes: {}, qtyNoSize: "" };
      if (!l.size || l.size === "-") groups[k].qtyNoSize = String((Number(groups[k].qtyNoSize) || 0) + Number(l.qty || 0));
      else groups[k].sizes[l.size] = String((Number(groups[k].sizes[l.size]) || 0) + Number(l.qty || 0));
    });
    return Object.values(groups);
  };

  const goToPengiriman = (q) => {
    navigate("/pengiriman", { state: { prefill: {
      requestId: q.id, requestNo: q.request_no, target: q.store,
      rows: buildPrefillRows(q),
    } } });
  };

  // P1: buka pengiriman yang sudah ada (cegah duplikat). Arahkan ke halaman Pengiriman
  // dengan sorotan Surat Jalan existing — tidak membuat shipment baru.
  const viewPengiriman = (q) => {
    navigate("/pengiriman", { state: { focusShipment: {
      shipmentId: q.active_shipment?.id, sjNo: q.active_shipment?.sj_no,
      storeRequestId: q.id, requestNo: q.request_no,
    } } });
  };

  const openStock = async (q) => {
    setStock({ request_no: q.request_no, loading: true, lines: [] });
    setStockLoading(true);
    try {
      const r = await api(`/api/v2/store-requests/${q.id}/stock-check`);
      if (r.ok) setStock(await r.json());
      else { toast.error("Gagal cek stok"); setStock(null); }
    } finally { setStockLoading(false); }
  };

  const doClose = async () => {
    await act(closeId, "close", { reason: closeReason });
    setCloseId(null); setCloseReason("");
  };

  // Fase 4C: aksi baris permintaan dipakai ulang di tabel (desktop) & card (mobile).
  const renderRowActions = (q, sfx = "") => (
    <>
      {canCreate && q.status === "DRAFT" && <>
        <Button size="sm" variant="outline" disabled={isBusy(q.id, "submit")} onClick={() => act(q.id, "submit")} data-testid={`v2-pb-submit${sfx}`}>
          {isBusy(q.id, "submit") ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Ajukan
        </Button>
        <Button size="sm" variant="ghost" onClick={() => startRevise(q)} data-testid={`v2-pb-revise${sfx}`}>
          <Pencil className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" className="text-destructive" disabled={isBusy(q.id, "cancel")} onClick={() => act(q.id, "cancel")} data-testid={`v2-pb-cancel${sfx}`}>
          {isBusy(q.id, "cancel") ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
        </Button>
      </>}
      {canCreate && q.status === "PENDING_APPROVAL" &&
        <Button size="sm" variant="ghost" className="text-destructive" disabled={isBusy(q.id, "cancel")} onClick={() => act(q.id, "cancel")} data-testid={`v2-pb-cancel${sfx}`}>
          {isBusy(q.id, "cancel") ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}Batal
        </Button>}
      {canCreate && q.status === "REJECTED" &&
        <Button size="sm" variant="outline" onClick={() => startRevise(q)} data-testid={`v2-pb-revise-rejected${sfx}`}>
          <Pencil className="w-4 h-4 mr-1" />Revisi
        </Button>}
      {canApprove && q.status === "PENDING_APPROVAL" && <>
        <Button size="sm" disabled={isBusy(q.id, "approve")} onClick={() => act(q.id, "approve")} data-testid={`v2-pb-approve${sfx}`}>
          {isBusy(q.id, "approve") ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Setujui
        </Button>
        <Button size="sm" variant="destructive" onClick={() => { setRejectId(q.id); setRejectReason(""); }} data-testid={`v2-pb-reject${sfx}`}>
          <X className="w-4 h-4 mr-1" />Tolak
        </Button>
      </>}
      {canApprove && APPROVED_FAMILY.includes(q.status) && <>
        <Button size="sm" variant="ghost" onClick={() => openStock(q)} data-testid={`v2-pb-stock-${q.id}${sfx}`}>
          <PackageSearch className="w-4 h-4 mr-1" />Cek Stok
        </Button>
        {q.active_shipment ? (
          <Button size="sm" variant="secondary" onClick={() => viewPengiriman(q)} data-testid={`v2-pb-view-shipment-${q.id}${sfx}`} title={`Surat Jalan ${q.active_shipment.sj_no}`}>
            <Truck className="w-4 h-4 mr-1" />Lihat Pengiriman
          </Button>
        ) : q.status === "MENUNGGU_PRODUKSI" ? (
          <Button size="sm" variant="outline" disabled={isBusy(q.id, "recheck-stock")} onClick={() => act(q.id, "recheck-stock")} data-testid={`v2-pb-recheck-${q.id}${sfx}`}>
            {isBusy(q.id, "recheck-stock") ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Recheck Stok
          </Button>
        ) : SHIPPABLE.includes(q.status) ? (
          <Button size="sm" onClick={() => goToPengiriman(q)} data-testid={`v2-pb-continue-${q.id}${sfx}`}>
            <Truck className="w-4 h-4 mr-1" />Lanjutkan ke Pengiriman Store
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => { setCloseId(q.id); setCloseReason(""); }} data-testid={`v2-pb-close-${q.id}${sfx}`}>
          <DoorClosed className="w-4 h-4 mr-1" />Tutup
        </Button>
      </>}
      {q.status !== "DRAFT" && (
        <Button size="sm" variant="ghost" onClick={() => openPdf(api, "store-request", q.id)} data-testid={`v2-pb-pdf-${q.id}${sfx}`} title="Preview / Download PDF">
          <FileText className="w-4 h-4" />
        </Button>
      )}
    </>
  );

  // Fase 4C: status + timeline ringkas (dipakai desktop & mobile).
  const renderStatusMeta = (q, sfx = "") => (
    <>
      <StatusBadge status={q.status} label={STATUS_LABEL[q.status] || q.status} testid={`v2-pb-status${sfx}`} />
      <div className="text-[10px] text-muted-foreground mt-1 space-y-0.5">
        {q.submitted_at && <div>Diajukan: {fmtTs(q.submitted_at)}</div>}
        {q.decided_at && <div>Diputuskan: {fmtTs(q.decided_at)}</div>}
        {APPROVED_FAMILY.includes(q.status) && typeof q.remaining_qty === "number" && (
          q.remaining_qty > 0
            ? <div className="text-[hsl(var(--badge-warning))]" data-testid={`v2-pb-remaining-${q.id}${sfx}`}>Belum terpenuhi: {q.remaining_qty} pcs</div>
            : <div className="text-[hsl(var(--badge-success))]" data-testid={`v2-pb-remaining-${q.id}${sfx}`}>Terpenuhi penuh</div>
        )}
        {q.status === "MENUNGGU_PRODUKSI" && (
          <div className="text-destructive" data-testid={`v2-pb-shortage-${q.id}${sfx}`}>
            Stok gudang kurang{typeof q.total_kurang === "number" ? ` ${q.total_kurang} pcs` : ""} — tunggu Admin Produksi buat Work Order
          </div>
        )}
        {q.status === "CLOSED" && q.close_reason && <div>Ditutup: {q.close_reason}</div>}
        {q.status === "REJECTED" && q.reject_reason && <div className="text-destructive">Alasan: {q.reject_reason}</div>}
      </div>
    </>
  );

  return (
    <div className="space-y-5" data-testid="v2-permintaan-page">
      <div className="flex items-center gap-2">
        <ClipboardList className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-semibold">Permintaan Merchandize</h2>
      </div>

      {/* ===== Form buat/revisi (Staff Store) — multi-line + size grid ===== */}
      {canCreate && (
        <Card className="p-5 rounded-2xl" data-testid="v2-pb-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">{editId ? "Revisi Permintaan" : "Buat Permintaan Baru"}</h3>
            <Badge variant="outline" data-testid="v2-pb-region">Region Store: {REGION_LABEL[myRegion] || "-"}</Badge>
          </div>

          <div className="space-y-3">
            {lines.map((l, idx) => {
              const p = productById(l.product_id);
              const owner = ownerOf(p);
              return (
                <div key={l.key} className="rounded-xl border p-3 space-y-3" data-testid="v2-pb-line">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-5">
                      <Label className="text-xs">Merchandize</Label>
                      <Select value={l.product_id} onValueChange={(v) => setLine(l.key, { product_id: v, sizes: {}, qtyNoSize: "" })}>
                        <SelectTrigger data-testid="v2-pb-line-merch"><SelectValue placeholder="Pilih Merchandize..." /></SelectTrigger>
                        <SelectContent>
                          {myProducts.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Tidak ada Merchandize aktif untuk region Anda</div>}
                          {myProducts.map((pr) => (
                            <SelectItem key={pr.id} value={pr.id} data-testid={`v2-pb-merch-${pr.id}`}>
                              {pr.name} — {pr.club}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs">Region Pemilik Merchandize</Label>
                      <div className="h-9 flex items-center" data-testid="v2-pb-line-owner">
                        {owner ? <Badge variant="secondary">{REGION_LABEL[owner] || owner}</Badge>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs">Jenis Stok</Label>
                      <Select value={l.item_category} onValueChange={(v) => setLine(l.key, { item_category: v })}>
                        <SelectTrigger data-testid="v2-pb-line-jenis"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Jual" data-testid="v2-pb-jenis-jual">JUAL</SelectItem>
                          <SelectItem value="MoU" data-testid="v2-pb-jenis-mou">MoU</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-1 flex justify-end">
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeLine(l.key)}
                        disabled={lines.length <= 1} data-testid="v2-pb-line-remove" title="Hapus baris">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {l.product_id && (
                    p && !p.has_size_variants ? (
                      <div className="max-w-[200px]">
                        <Label className="text-xs">Quantity</Label>
                        <Input type="number" min="0" value={l.qtyNoSize}
                          onChange={(e) => setLine(l.key, { qtyNoSize: e.target.value })}
                          placeholder="0" data-testid="v2-pb-qty-nosize" />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-xs">Size &amp; Quantity</Label>
                        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mt-1">
                          {SIZES.map((sz) => (
                            <div key={sz}>
                              <div className="text-[10px] text-center text-muted-foreground">{sz}</div>
                              <Input type="number" min="0" value={l.sizes[sz] || ""}
                                onChange={(e) => setSizeQty(l.key, sz, e.target.value)}
                                className="text-center px-1" placeholder="0" data-testid={`v2-pb-size-${sz}`} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  )}

                  <div className="text-xs text-muted-foreground">Total baris #{idx + 1}: <b>{lineTotal(l)}</b> pcs</div>
                </div>
              );
            })}
          </div>

          <div className="mt-3">
            <Label className="text-xs">Catatan (opsional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="Catatan permintaan..." data-testid="v2-pb-note" />
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setLines((ls) => [...ls, newLine()])} data-testid="v2-pb-add-line">
              <Plus className="w-4 h-4 mr-1" />Tambah Merchandize
            </Button>
            <div className="flex-1" />
            <Button onClick={submitForm} disabled={submitting} data-testid="v2-pb-save">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              {submitting ? "Menyimpan…" : (editId ? "Simpan Revisi" : "Buat Permintaan (Draft)")}
            </Button>
            {editId && <Button variant="ghost" onClick={resetForm} data-testid="v2-pb-cancel-edit">Batal Edit</Button>}
          </div>
        </Card>
      )}

      {/* ===== Daftar permintaan ===== */}
      <Card className="p-5 rounded-2xl" ref={listTopRef}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Daftar Permintaan <span className="text-sm font-normal text-muted-foreground">({meta.total})</span></h3>
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>No</TableHead><TableHead>Store</TableHead><TableHead>Merchandize</TableHead>
              <TableHead>Qty</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {reqs.map((q) => (
                <TableRow key={q.id} data-testid="v2-pb-row">
                  <TableCell className="font-mono text-xs">{q.request_no}</TableCell>
                  <TableCell>{q.store}</TableCell>
                  <TableCell>
                    <Button variant="link" className="h-auto p-0 text-sm" onClick={() => setDetail(q)}
                      data-testid={`v2-pb-detail-${q.id}`}>
                      {(q.lines || []).length} item
                    </Button>
                  </TableCell>
                  <TableCell>{q.total_qty}</TableCell>
                  <TableCell>{renderStatusMeta(q)}</TableCell>
                  <TableCell className="text-right space-x-1">{renderRowActions(q)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile: card list permintaan */}
        <div className="md:hidden space-y-3" data-testid="v2-pb-cards">
          {reqs.map((q) => (
            <div key={q.id} className="rounded-xl border p-3 space-y-2" data-testid="v2-pb-row-m">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs">{q.request_no}</span>
                <Badge variant="outline">{q.store}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <Button variant="link" className="h-auto p-0 text-sm" onClick={() => setDetail(q)} data-testid={`v2-pb-detail-${q.id}-m`}>
                  {(q.lines || []).length} item
                </Button>
                <span className="text-muted-foreground">Qty: <b className="text-foreground">{q.total_qty}</b></span>
              </div>
              <div>{renderStatusMeta(q, "-m")}</div>
              <div className="flex flex-wrap gap-2 pt-1 border-t">{renderRowActions(q, "-m")}</div>
            </div>
          ))}
        </div>
        <ListPager
          page={page} pageSize={pageSize} total={meta.total} pages={meta.pages} loading={loading}
          onPageChange={goPage} onPageSizeChange={changePageSize}
          storageKey="store-requests" testid="permintaan-pager"
        />
      </Card>

      {/* Dialog Tolak */}
      <Dialog open={!!rejectId} onOpenChange={(o) => !o && setRejectId(null)}>
        <DialogContent data-testid="v2-pb-reject-dialog">
          <DialogHeader><DialogTitle>Tolak Permintaan</DialogTitle></DialogHeader>
          <Label className="text-xs">Alasan penolakan</Label>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3}
            placeholder="Mis. stok masih cukup" data-testid="v2-pb-reject-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectId(null)} data-testid="v2-pb-reject-cancel">Batal</Button>
            <Button variant="destructive" disabled={busyAct === `${rejectId}:reject`} onClick={doReject} data-testid="v2-pb-reject-confirm">{busyAct === `${rejectId}:reject` && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Tolak Permintaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Detail item */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="v2-pb-detail-dialog">
          <DialogHeader><DialogTitle>Detail Permintaan {detail?.request_no}</DialogTitle></DialogHeader>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Club</TableHead><TableHead>Jenis Stok</TableHead><TableHead>Size</TableHead><TableHead>Qty</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(detail?.lines || []).map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.club}</TableCell>
                  <TableCell><CategoryBadge cat={l.item_category} /></TableCell>
                  <TableCell>{l.size}</TableCell><TableCell>{l.qty}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {detail?.note && <p className="text-sm text-muted-foreground">Catatan: {detail.note}</p>}
        </DialogContent>
      </Dialog>

      {/* UAT-05: Dialog Cek Stok Regional (informasi saja) */}
      <Dialog open={!!stock} onOpenChange={(o) => !o && setStock(null)}>
        <DialogContent className="max-w-2xl" data-testid="v2-pb-stock-dialog">
          <DialogHeader><DialogTitle>Cek Stok Gudang · {stock?.request_no}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            Stok gudang <b>{stock?.gudang_sumber || "-"}</b> (informasi saja — sistem TIDAK membuat Work Order otomatis).
            Keputusan produksi/pengiriman tetap di tangan Kepala Cabang.
          </p>
          {stockLoading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : (
            <>
              <div className="flex gap-2 flex-wrap text-xs">
                <Badge variant="outline" data-testid="v2-pb-stock-total-req">Diminta: {stock?.total_diminta ?? 0}</Badge>
                <Badge variant="secondary" data-testid="v2-pb-stock-total-avail">Tersedia: {stock?.total_tersedia ?? 0}</Badge>
                <Badge variant={(stock?.total_kurang ?? 0) > 0 ? "destructive" : "default"} data-testid="v2-pb-stock-total-short">Kurang: {stock?.total_kurang ?? 0}</Badge>
              </div>
              <div className="overflow-x-auto mt-2">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Merchandize</TableHead><TableHead>Jenis</TableHead><TableHead>Size</TableHead>
                    <TableHead className="text-right">Diminta</TableHead><TableHead className="text-right">Tersedia</TableHead><TableHead className="text-right">Kurang</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(stock?.lines || []).map((l, i) => (
                      <TableRow key={i} data-testid="v2-pb-stock-row">
                        <TableCell>{l.merchandize}</TableCell>
                        <TableCell><Badge variant={l.jenis_stok === "MoU" ? "secondary" : "outline"}>{l.jenis_stok}</Badge></TableCell>
                        <TableCell>{l.size}</TableCell>
                        <TableCell className="text-right">{l.qty_diminta}</TableCell>
                        <TableCell className="text-right">{l.qty_tersedia}</TableCell>
                        <TableCell className={`text-right ${l.qty_kurang > 0 ? "text-destructive font-semibold" : ""}`}>{l.qty_kurang}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* UAT-05: Dialog Tutup Permintaan */}
      <Dialog open={!!closeId} onOpenChange={(o) => !o && setCloseId(null)}>
        <DialogContent data-testid="v2-pb-close-dialog">
          <DialogHeader><DialogTitle>Tutup Permintaan</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            Permintaan ditutup tanpa dilanjutkan ke Pengiriman Store. Task follow-up akan hilang.
          </p>
          <Label className="text-xs">Alasan (opsional)</Label>
          <Textarea value={closeReason} onChange={(e) => setCloseReason(e.target.value)} rows={3}
            placeholder="Mis. stok belum cukup, akan diproduksi dulu" data-testid="v2-pb-close-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCloseId(null)} data-testid="v2-pb-close-cancel">Batal</Button>
            <Button variant="destructive" disabled={busyAct === `${closeId}:close`} onClick={doClose} data-testid="v2-pb-close-confirm">{busyAct === `${closeId}:close` && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Tutup Permintaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
